import type { ReactNode } from "react";
import { getStrings, homeShellString } from "../i18n/strings";

type Strings = ReturnType<typeof getStrings>;

type DetectIconType = "reputation" | "proxy-vpn" | "tor" | "network" | "threat" | "infra";

// "What NetRiskScan Detects" — six short capability cards, each with a small colored inline SVG
// icon. Pure/stateless: also rendered at build time into web/index.html's static markup (see
// vite.config.ts) — the SEO snapshot renders its own plain markup for the same six titles/texts and
// does not need to reproduce these decorative icons.
export function HomeDetects({ strings }: { strings: Strings }) {
  const t = <K extends Parameters<typeof homeShellString>[1]>(key: K) => homeShellString(strings, key);

  const cards: Array<{ icon: DetectIconType; title: string; text: string }> = [
    { icon: "reputation", title: t("homeDetectsReputationTitle"), text: t("homeDetectsReputationText") },
    { icon: "proxy-vpn", title: t("homeDetectsProxyVpnTitle"), text: t("homeDetectsProxyVpnText") },
    { icon: "tor", title: t("homeDetectsTorTitle"), text: t("homeDetectsTorText") },
    { icon: "network", title: t("homeDetectsNetworkTitle"), text: t("homeDetectsNetworkText") },
    { icon: "threat", title: t("homeDetectsThreatTitle"), text: t("homeDetectsThreatText") },
    { icon: "infra", title: t("homeDetectsInfraTitle"), text: t("homeDetectsInfraText") },
  ];

  return (
    <section className="home-detects" aria-labelledby="home-detects-title">
      <h2 id="home-detects-title" className="home-section-title">{t("homeDetectsTitle")}</h2>
      <div className="home-detects__panel">
        <div className="home-detects__grid">
          {cards.map((card) => (
            <article className={`home-detects__card home-detects__card--${card.icon}`} key={card.title}>
              <span className="home-detects__icon" aria-hidden="true"><DetectIcon type={card.icon} /></span>
              <h3>{card.title}</h3>
              <p>{card.text}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function DetectIcon({ type }: { type: DetectIconType }) {
  const paths: Record<DetectIconType, ReactNode> = {
    reputation: <><path d="M12 3 5 6v5c0 4.5 3 7.5 7 9 4-1.5 7-4.5 7-9V6l-7-3Z" /><path d="m9 12 2 2 4-4" /></>,
    "proxy-vpn": <><rect x="3" y="6" width="18" height="12" rx="2.5" /><path d="M8 12h.01M12 12h.01M16 12h.01" /></>,
    tor: <><circle cx="12" cy="12" r="8.5" /><circle cx="12" cy="12" r="3.2" /><path d="M12 3.5v5.3M12 15.2v5.3M3.5 12h5.3M15.2 12h5.3" /></>,
    network: <><circle cx="5" cy="12" r="2.2" /><circle cx="19" cy="6" r="2.2" /><circle cx="19" cy="18" r="2.2" /><path d="M7 11l10-4M7 13l10 4" /></>,
    threat: <><path d="M12 3 5 6v5c0 4.5 3 7.5 7 9 4-1.5 7-4.5 7-9V6l-7-3Z" /><path d="M12 8.5v4M12 15.5h.01" /></>,
    infra: <><rect x="3" y="4" width="18" height="7" rx="1.5" /><rect x="3" y="13" width="18" height="7" rx="1.5" /><path d="M7 7.5h.01M7 16.5h.01M7 9.5v0M17 7.5h.01" /></>,
  };
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {paths[type]}
    </svg>
  );
}
